import { GameAudio } from "./Audio";
import {
  BETWEEN_WAVE_GRACE,
  COLS,
  ENEMY_SPECS,
  FIRST_WAVE_DELAY,
  FIXED_STEP,
  HEIGHT,
  PATH_POINTS,
  POWER_SPECS,
  ROWS,
  START_CASH,
  START_LIVES,
  TILE,
  TOWER_SPECS,
  WIDTH,
  SeededRandom,
  bountyFor,
  comboMultiplier,
  hpScale,
  isBossWave,
  isBuildable,
  parseDefenseSave,
  pickQuip,
  randomSeed,
  refundFor,
  speedScale,
  upgradeCost,
  waveBonus,
  waveComposition,
  type EnemySpec,
  type EnemyTypeId,
  type PowerId,
  type TowerTier,
  type TowerTypeId,
} from "./rules";
import { readStoredValue, writeStoredValue } from "./storage";

const SAVE_KEY = "immigrant-defense:save";
const SOUND_KEY = "immigrant-defense:sound";

interface GameRefs {
  shell: HTMLElement;
  canvas: HTMLCanvasElement;
  overlay: HTMLElement;
  banner: HTMLElement;
  cash: HTMLElement;
  wave: HTMLElement;
  lives: HTMLElement;
  streak: HTMLElement;
  score: HTMLElement;
  best: HTMLElement;
  buildBar: HTMLElement;
  towerPanel: HTMLElement;
  nextWaveButton: HTMLButtonElement;
  pauseButton: HTMLButtonElement;
  soundButton: HTMLButtonElement;
  fullscreenButton: HTMLButtonElement;
}

interface Tower {
  col: number;
  row: number;
  x: number;
  y: number;
  type: TowerTypeId;
  tier: number;
  cooldown: number;
  recoil: number;
  aim: number;
}

interface Enemy {
  id: number;
  type: EnemyTypeId;
  spec: EnemySpec;
  hp: number;
  maxHp: number;
  x: number;
  y: number;
  wp: number;
  dist: number;
  flashUntil: number;
  slowed: boolean;
  dead: boolean;
}

interface Shell {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  age: number;
  duration: number;
  damage: number;
  splash: number;
  cluster: number;
}

interface Tracer {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  age: number;
  ttl: number;
  color: string;
}

interface FloatText {
  x: number;
  y: number;
  text: string;
  color: string;
  size: number;
  age: number;
  ttl: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  age: number;
  ttl: number;
}

interface Blast {
  x: number;
  y: number;
  radius: number;
  age: number;
  ttl: number;
  color: string;
}

interface GasCloud {
  x: number;
  y: number;
  radius: number;
  until: number;
}

interface SpawnEvent {
  at: number;
  type: EnemyTypeId;
}

type Phase = "menu" | "playing" | "paused" | "over";

const ENEMY_COLORS: Record<EnemyTypeId, { body: string; trim: string }> = {
  crosser: { body: "#c98f4e", trim: "#7a4a22" },
  sprinter: { body: "#e3c268", trim: "#8a6a20" },
  caravan: { body: "#8a5a3a", trim: "#4e2e18" },
  splitter: { body: "#b0769b", trim: "#5e3a52" },
  kid: { body: "#d8a7c4", trim: "#7a4a66" },
  boss: { body: "#3f6d8e", trim: "#1d3547" },
};

const TOWER_COLORS: Record<TowerTypeId, string> = {
  watchtower: "#7c5a34",
  sniper: "#5a6570",
  checkpoint: "#c9a227",
  mortar: "#4a4f42",
  tesla: "#3f7ea6",
  orbital: "#a8893a",
};

const GUN_PIVOT_Y: Record<TowerTypeId, number> = {
  watchtower: -11,
  sniper: 0,
  checkpoint: 0,
  mortar: 0,
  tesla: 0,
  orbital: 0,
};

function tilesOf(range: number): string {
  return String(Math.round((range / TILE) * 10) / 10);
}

function statDeltas(current: TowerTier, next: TowerTier): string[] {
  const rows: string[] = [];
  if (next.damage !== current.damage) rows.push(`DMG ${current.damage} → ${next.damage}`);
  if (next.range !== current.range && next.range < 1000) rows.push(`RNG ${tilesOf(current.range)} → ${tilesOf(next.range)} tiles`);
  if (next.fireRate !== current.fireRate) rows.push(`RATE ${current.fireRate}/s → ${next.fireRate}/s`);
  if (next.splash !== current.splash) rows.push(`SPLASH ${current.splash} → ${next.splash}`);
  if (next.slowFactor !== current.slowFactor) {
    rows.push(`SLOW ${Math.round((1 - current.slowFactor) * 100)}% → ${Math.round((1 - next.slowFactor) * 100)}%`);
  }
  if ((next.targets ?? 1) !== (current.targets ?? 1)) rows.push(`TARGETS ${current.targets ?? 1} → ${next.targets ?? 1}`);
  if ((next.pierce ?? 1) !== (current.pierce ?? 1)) rows.push(`PIERCE ${current.pierce ?? 1} → ${next.pierce ?? 1}`);
  if ((next.chain ?? 0) !== (current.chain ?? 0)) rows.push(`CHAIN ${current.chain ?? 0} → ${next.chain ?? 0}`);
  if ((next.cluster ?? 0) !== (current.cluster ?? 0)) rows.push(`CLUSTER +${next.cluster ?? 0} sub-bombs`);
  return rows;
}

function angleDelta(a: number, b: number): number {
  return Math.abs(((a - b + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
}

function turnToward(current: number, target: number, maxStep: number): number {
  const delta = ((target - current + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
  return Math.abs(delta) <= maxStep ? target : current + Math.sign(delta) * maxStep;
}

export class ImmigrantDefenseGame {
  private readonly refs: GameRefs;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly audio: GameAudio;
  private readonly random = new SeededRandom(randomSeed());
  private readonly towerButtons = new Map<TowerTypeId, HTMLButtonElement>();
  private readonly powerButtons = new Map<PowerId, HTMLButtonElement>();

  private phase: Phase = "menu";
  private time = 0;
  private clockSeconds = 0;
  private cash = START_CASH;
  private lives = START_LIVES;
  private score = 0;
  private bestScore = 0;
  private bestWave = 0;
  private streak = 0;
  private wave = 0;
  private waveActive = false;
  private waveTime = 0;
  private idleTimer = 0;
  private spawnSchedule: SpawnEvent[] = [];
  private spawnIndex = 0;
  private nextEnemyId = 1;
  private shake = 0;
  private soundOn = true;

  private towers: Tower[] = [];
  private enemies: Enemy[] = [];
  private shells: Shell[] = [];
  private tracers: Tracer[] = [];
  private floats: FloatText[] = [];
  private particles: Particle[] = [];
  private blasts: Blast[] = [];
  private gasClouds: GasCloud[] = [];

  private placing: TowerTypeId | null = null;
  private armedPower: PowerId | null = null;
  private selected: Tower | null = null;
  private hover: { col: number; row: number } | null = null;
  private powerReadyAt: Record<PowerId, number> = { drone: 0, teargas: 0 };

  private rafId = 0;
  private lastFrame = 0;
  private accumulator = 0;
  private hudDirty = true;
  private rotated = false;

  constructor(refs: GameRefs) {
    this.refs = refs;
    const ctx = refs.canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    this.ctx = ctx;

    this.soundOn = readStoredValue(SOUND_KEY) !== "0";
    this.audio = new GameAudio(this.soundOn);

    const save = parseDefenseSave(readStoredValue(SAVE_KEY));
    this.bestScore = save.bestScore;
    this.bestWave = save.bestWave;

    this.buildBuildBar();
    this.bindInput();
    this.resize();
    window.addEventListener("resize", () => this.resize());

    this.showMenu();
    this.updateSoundButton();
    this.rafId = requestAnimationFrame((now) => this.frame(now));
  }

  destroy(): void {
    cancelAnimationFrame(this.rafId);
  }

  private buildBuildBar(): void {
    for (const spec of Object.values(TOWER_SPECS)) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tower-card";
      button.dataset.tower = spec.id;
      button.innerHTML = `<span class="tower-icon">${spec.icon}</span><span class="tower-name">${spec.label}</span><span class="tower-cost">$${spec.tiers[0]?.cost ?? 0}</span>`;
      button.title = spec.blurb;
      if (spec.prestige) button.classList.add("prestige");
      button.addEventListener("click", () => this.togglePlacing(spec.id));
      this.refs.buildBar.appendChild(button);
      this.towerButtons.set(spec.id, button);
    }

    for (const spec of Object.values(POWER_SPECS)) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "power-card";
      button.dataset.power = spec.id;
      button.innerHTML = `<span class="tower-icon">${spec.icon}</span><span class="tower-name">${spec.label}</span><span class="tower-cost power-cooldown">READY</span>`;
      button.title = spec.blurb;
      button.addEventListener("click", () => this.togglePower(spec.id));
      this.refs.buildBar.appendChild(button);
      this.powerButtons.set(spec.id, button);
    }
  }

  private bindInput(): void {
    const { canvas, pauseButton, soundButton, fullscreenButton, nextWaveButton } = this.refs;

    canvas.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      void this.audio.unlock();
      const point = this.toGamePoint(event);
      this.handleFieldTap(point.x, point.y);
    });
    canvas.addEventListener("pointermove", (event) => {
      const point = this.toGamePoint(event);
      const col = Math.floor(point.x / TILE);
      const row = Math.floor(point.y / TILE);
      this.hover = col >= 0 && col < COLS && row >= 0 && row < ROWS ? { col, row } : null;
    });
    canvas.addEventListener("pointerleave", () => {
      this.hover = null;
    });

    pauseButton.addEventListener("click", () => this.togglePause());
    soundButton.addEventListener("click", () => this.toggleSound());
    fullscreenButton.addEventListener("click", () => {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        void this.refs.shell.requestFullscreen?.();
      }
    });
    nextWaveButton.addEventListener("click", () => {
      if (this.phase === "playing" && !this.waveActive) this.startWave();
    });

    window.addEventListener("keydown", (event) => {
      if (event.key === "p" || event.key === "P" || event.key === "Escape") this.togglePause();
      if (event.key === "m" || event.key === "M") this.toggleSound();
      if (event.key === "Enter" && (this.phase === "menu" || this.phase === "over")) this.startRun();
    });
  }

  private toGamePoint(event: PointerEvent): { x: number; y: number } {
    const rect = this.refs.canvas.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * (this.rotated ? HEIGHT : WIDTH);
    const py = ((event.clientY - rect.top) / rect.height) * (this.rotated ? WIDTH : HEIGHT);
    return this.rotated ? { x: py, y: HEIGHT - px } : { x: px, y: py };
  }

  private handleFieldTap(x: number, y: number): void {
    if (this.phase !== "playing") return;

    if (this.armedPower) {
      this.firePower(this.armedPower, x, y);
      return;
    }

    const col = Math.floor(x / TILE);
    const row = Math.floor(y / TILE);

    if (this.placing) {
      this.tryPlace(col, row);
      return;
    }

    const hit = this.towers.find((tower) => tower.col === col && tower.row === row);
    this.selected = hit ?? null;
    this.renderTowerPanel();
  }

  private togglePlacing(type: TowerTypeId): void {
    if (this.phase !== "playing") return;
    this.placing = this.placing === type ? null : type;
    this.armedPower = null;
    this.selected = null;
    this.renderTowerPanel();
    this.syncBuildBar();
  }

  private togglePower(power: PowerId): void {
    if (this.phase !== "playing") return;
    if (this.time < this.powerReadyAt[power]) {
      this.audio.denied();
      return;
    }
    this.armedPower = this.armedPower === power ? null : power;
    this.placing = null;
    this.selected = null;
    this.renderTowerPanel();
    this.syncBuildBar();
  }

  private tryPlace(col: number, row: number): void {
    const type = this.placing;
    if (!type) return;
    const spec = TOWER_SPECS[type];
    const cost = spec.tiers[0]?.cost ?? 0;
    const occupied = this.towers.some((tower) => tower.col === col && tower.row === row);

    if (!isBuildable(col, row) || occupied) {
      this.audio.denied();
      this.spawnFloat("BLOCKED", (col + 0.5) * TILE, (row + 0.5) * TILE, "#ff7a6b", 18);
      return;
    }
    if (this.cash < cost) {
      this.audio.denied();
      this.spawnFloat("NO CASH", (col + 0.5) * TILE, (row + 0.5) * TILE, "#ff7a6b", 18);
      return;
    }

    this.cash -= cost;
    this.towers.push({
      col,
      row,
      x: (col + 0.5) * TILE,
      y: (row + 0.5) * TILE,
      type,
      tier: 0,
      cooldown: 0,
      recoil: 0,
      aim: Math.PI,
    });
    this.audio.place();
    this.burst((col + 0.5) * TILE, (row + 0.5) * TILE, "#d8c9a3", 8, 90);
    this.hudDirty = true;
    this.syncBuildBar();

    if (this.cash < cost) this.placing = null;
    this.syncBuildBar();
  }

  private upgradeSelected(): void {
    const tower = this.selected;
    if (!tower) return;
    const spec = TOWER_SPECS[tower.type];
    const cost = upgradeCost(spec, tower.tier);
    if (cost === null || this.cash < cost) {
      this.audio.denied();
      return;
    }
    this.cash -= cost;
    tower.tier += 1;
    const reached = spec.tiers[tower.tier];
    this.audio.upgrade();
    this.blasts.push({ x: tower.x, y: tower.y, radius: 54, age: 0, ttl: 0.4, color: "#ffe08a" });
    this.burst(tower.x, tower.y, "#ffe08a", 18, 150);
    this.spawnFloat(reached ? `→ ${reached.name.toUpperCase()}!` : "UPGRADED!", tower.x, tower.y - 24, "#ffe08a", 18);
    this.hudDirty = true;
    this.renderTowerPanel();
    this.syncBuildBar();
  }

  private sellSelected(): void {
    const tower = this.selected;
    if (!tower) return;
    const refund = refundFor(TOWER_SPECS[tower.type], tower.tier);
    this.cash += refund;
    this.towers = this.towers.filter((other) => other !== tower);
    this.selected = null;
    this.audio.place();
    this.spawnFloat(`+$${refund}`, tower.x, tower.y - 20, "#9be27a", 18);
    this.hudDirty = true;
    this.renderTowerPanel();
    this.syncBuildBar();
  }

  private renderTowerPanel(): void {
    const panel = this.refs.towerPanel;
    const tower = this.selected;
    if (!tower) {
      panel.classList.remove("open");
      this.fitStageToViewport();
      panel.innerHTML = "";
      return;
    }
    const spec = TOWER_SPECS[tower.type];
    const tier = spec.tiers[tower.tier];
    const next = upgradeCost(spec, tower.tier);
    const refund = refundFor(spec, tower.tier);
    const nextTier = spec.tiers[tower.tier + 1];
    const pips = spec.tiers.map((_, index) => (index <= tower.tier ? "●" : "○")).join("");
    const deltas = tier && nextTier ? statDeltas(tier, nextTier) : [];
    panel.innerHTML = `
      <div class="panel-info">
        <strong>${spec.icon} ${tier?.name ?? spec.label}</strong>
        <span class="panel-pips">${pips}</span>
      </div>
      ${deltas.length > 0 ? `<div class="panel-deltas">${deltas.map((line) => `<span>${line}</span>`).join("")}</div>` : ""}
      ${nextTier?.perk ? `<div class="panel-perk">✦ ${nextTier.perk}</div>` : ""}
      <div class="panel-actions">
        ${next !== null ? `<button type="button" data-action="upgrade"${this.cash < next ? ` class="broke"` : ""}>UPGRADE $${next.toLocaleString()}</button>` : `<button type="button" disabled>MAX TIER</button>`}
        <button type="button" data-action="sell" class="sell">SELL +$${refund.toLocaleString()}</button>
      </div>
    `;
    panel.classList.add("open");
    this.fitStageToViewport();
    panel.querySelector('[data-action="upgrade"]')?.addEventListener("click", () => this.upgradeSelected());
    panel.querySelector('[data-action="sell"]')?.addEventListener("click", () => this.sellSelected());
  }

  private syncPanelAffordability(): void {
    if (!this.selected) return;
    const button = this.refs.towerPanel.querySelector<HTMLButtonElement>('[data-action="upgrade"]');
    if (!button) return;
    const cost = upgradeCost(TOWER_SPECS[this.selected.type], this.selected.tier);
    if (cost !== null) button.classList.toggle("broke", this.cash < cost);
  }

  private firePower(power: PowerId, x: number, y: number): void {
    const spec = POWER_SPECS[power];
    if (this.time < this.powerReadyAt[power]) {
      this.audio.denied();
      return;
    }
    this.powerReadyAt[power] = this.time + spec.cooldown;
    this.armedPower = null;

    if (power === "drone") {
      this.blasts.push({ x, y, radius: spec.radius, age: 0, ttl: 0.5, color: "#ffb347" });
      this.burst(x, y, "#ffb347", 26, 260);
      this.burst(x, y, "#ff6b4a", 18, 180);
      this.shake = Math.max(this.shake, 14);
      this.audio.explosion(1);
      for (const enemy of this.enemies) {
        if (Math.hypot(enemy.x - x, enemy.y - y) <= spec.radius + enemy.spec.radius) {
          this.damageEnemy(enemy, spec.damage, false);
        }
      }
      this.spawnFloat("DRONE STRIKE!", x, y - 40, "#ffb347", 26);
    } else {
      this.gasClouds.push({ x, y, radius: spec.radius, until: this.time + spec.slowDuration });
      this.burst(x, y, "#a8d97a", 20, 120);
      this.audio.explosion(0.4);
      this.spawnFloat("TEAR GAS!", x, y - 40, "#c9f29b", 24);
    }
    this.sweepDead();
    this.syncBuildBar();
  }

  private showMenu(): void {
    this.phase = "menu";
    this.refs.shell.dataset.state = "menu";
    this.refs.overlay.innerHTML = `
      <div class="menu-card">
        <p class="menu-kicker">BITGATE BORDER AUTHORITY PRESENTS</p>
        <h1>IMMIGRANT<br />DEFENSE</h1>
        <p class="menu-tagline">They're coming in waves. Literally. Hold the wall.</p>
        <ul class="menu-rules">
          <li><b>BUILD</b> towers next to the path — tap a card, tap a tile</li>
          <li><b>STOP</b> crossers before they reach the wall — ${START_LIVES} crossings and it's over</li>
          <li><b>ZAP</b> them with drone strikes &amp; tear gas when it gets spicy</li>
          <li><b>STREAK</b> kills without a breach for up to ×5 cash</li>
        </ul>
        <button type="button" class="big-start" data-action="start">START PATROL</button>
        <p class="menu-best">${this.bestScore > 0 ? `BEST: ${this.bestScore.toLocaleString()} pts · wave ${this.bestWave}` : "No patrols on record yet, rookie."}</p>
      </div>
    `;
    this.refs.overlay.querySelector('[data-action="start"]')?.addEventListener("click", () => {
      void this.audio.unlock();
      this.startRun();
    });
  }

  private startRun(): void {
    this.phase = "playing";
    this.refs.shell.dataset.state = "playing";
    this.refs.overlay.innerHTML = "";
    this.time = 0;
    this.clockSeconds = 0;
    this.cash = START_CASH;
    this.lives = START_LIVES;
    this.score = 0;
    this.streak = 0;
    this.wave = 0;
    this.waveActive = false;
    this.idleTimer = FIRST_WAVE_DELAY;
    this.towers = [];
    this.enemies = [];
    this.shells = [];
    this.tracers = [];
    this.floats = [];
    this.particles = [];
    this.blasts = [];
    this.gasClouds = [];
    this.spawnSchedule = [];
    this.spawnIndex = 0;
    this.selected = null;
    this.placing = null;
    this.armedPower = null;
    this.powerReadyAt = { drone: 0, teargas: 0 };
    this.shake = 0;
    this.hudDirty = true;
    this.renderTowerPanel();
    this.syncBuildBar();
    this.showBanner("SHIFT STARTED — FIRST WAVE INCOMING", 1.8);
  }

  private togglePause(): void {
    if (this.phase === "playing") {
      this.phase = "paused";
      this.refs.shell.dataset.state = "paused";
      this.refs.overlay.innerHTML = `
        <div class="menu-card compact">
          <h2>COFFEE BREAK</h2>
          <p class="menu-tagline">The border holds itself. For now.</p>
          <button type="button" class="big-start" data-action="resume">RESUME PATROL</button>
        </div>
      `;
      this.refs.overlay.querySelector('[data-action="resume"]')?.addEventListener("click", () => this.togglePause());
    } else if (this.phase === "paused") {
      this.phase = "playing";
      this.refs.shell.dataset.state = "playing";
      this.refs.overlay.innerHTML = "";
    }
  }

  private toggleSound(): void {
    this.soundOn = !this.soundOn;
    this.audio.setEnabled(this.soundOn);
    writeStoredValue(SOUND_KEY, this.soundOn ? "1" : "0");
    this.updateSoundButton();
  }

  private updateSoundButton(): void {
    this.refs.soundButton.textContent = this.soundOn ? "◖))" : "◖✕";
    this.refs.soundButton.setAttribute("aria-label", this.soundOn ? "Mute sound" : "Unmute sound");
  }

  private startWave(): void {
    this.wave += 1;
    this.waveActive = true;
    this.waveTime = 0;
    this.spawnIndex = 0;
    this.spawnSchedule = [];
    for (const group of waveComposition(this.wave)) {
      for (let index = 0; index < group.count; index += 1) {
        this.spawnSchedule.push({ at: group.delay + index * group.gap, type: group.type });
      }
    }
    this.spawnSchedule.sort((a, b) => a.at - b.at);
    this.hudDirty = true;
    this.refs.nextWaveButton.disabled = true;

    if (isBossWave(this.wave)) {
      this.showBanner(`⚠ WAVE ${this.wave} — COYOTE CONVOY INBOUND ⚠`, 2.4, true);
      this.audio.bossWarning();
    } else {
      this.showBanner(`WAVE ${this.wave}`, 1.2);
    }
  }

  private clearWave(): void {
    this.waveActive = false;
    this.idleTimer = BETWEEN_WAVE_GRACE;
    const bonus = waveBonus(this.wave);
    this.cash += bonus;
    this.score += this.wave * 50;
    this.audio.waveClear();
    this.showBanner(`WAVE ${this.wave} CLEARED — +$${bonus} BUDGET SURPLUS`, 1.8);
    this.hudDirty = true;
    this.persistBest();
  }

  private gameOver(): void {
    this.phase = "over";
    this.refs.shell.dataset.state = "over";
    this.audio.gameOver();
    this.persistBest();
    this.refs.overlay.innerHTML = `
      <div class="menu-card compact">
        <p class="menu-kicker">THE WALL HAS FALLEN</p>
        <h1>BORDER<br />BREACHED</h1>
        <p class="menu-tagline">Wave ${this.wave} · ${this.score.toLocaleString()} pts · best ${this.bestScore.toLocaleString()} pts (wave ${this.bestWave})</p>
        <button type="button" class="big-start" data-action="restart">RE-DEPLOY</button>
        <p class="menu-best">Press <kbd>Enter</kbd> to restart</p>
      </div>
    `;
    this.refs.overlay.querySelector('[data-action="restart"]')?.addEventListener("click", () => this.startRun());
  }

  private persistBest(): void {
    const betterScore = Math.max(this.bestScore, this.score);
    const betterWave = Math.max(this.bestWave, this.wave);
    if (betterScore !== this.bestScore || betterWave !== this.bestWave) {
      this.bestScore = betterScore;
      this.bestWave = betterWave;
      writeStoredValue(SAVE_KEY, JSON.stringify({ version: 1, bestScore: this.bestScore, bestWave: this.bestWave }));
      this.hudDirty = true;
    }
  }

  private showBanner(text: string, seconds: number, alarm = false): void {
    const banner = this.refs.banner;
    banner.textContent = text;
    banner.classList.toggle("alarm", alarm);
    banner.classList.add("show");
    window.setTimeout(() => banner.classList.remove("show"), seconds * 1000);
  }

  private frame(now: number): void {
    this.rafId = requestAnimationFrame((next) => this.frame(next));
    if (this.lastFrame === 0) this.lastFrame = now;
    const elapsed = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    if (this.phase === "playing") {
      this.accumulator += elapsed;
      while (this.accumulator >= FIXED_STEP) {
        this.step(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
      }
    }
    this.render();
    if (this.hudDirty) this.syncHud();
  }

  private step(dt: number): void {
    this.time += dt;
    this.clockSeconds += dt;
    this.shake = Math.max(0, this.shake - dt * 30);

    if (!this.waveActive) {
      this.idleTimer -= dt;
      this.syncNextWaveButton();
      if (this.idleTimer <= 0) this.startWave();
    } else {
      this.waveTime += dt;
      while (this.spawnIndex < this.spawnSchedule.length) {
        const event = this.spawnSchedule[this.spawnIndex];
        if (!event || event.at > this.waveTime) break;
        this.spawnEnemy(event.type);
        this.spawnIndex += 1;
      }
      if (this.spawnIndex >= this.spawnSchedule.length && this.enemies.length === 0) {
        this.clearWave();
      }
    }

    this.stepEnemies(dt);
    this.stepTowers(dt);
    this.stepShells(dt);
    this.sweepDead();

    this.tracers = this.tracers.filter((tracer) => (tracer.age += dt) < tracer.ttl);
    this.floats = this.floats.filter((float) => (float.age += dt) < float.ttl);
    this.blasts = this.blasts.filter((blast) => (blast.age += dt) < blast.ttl);
    this.gasClouds = this.gasClouds.filter((cloud) => cloud.until > this.time);
    this.particles = this.particles.filter((particle) => {
      particle.age += dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vx *= 0.96;
      particle.vy *= 0.96;
      return particle.age < particle.ttl;
    });

    this.syncPowerButtons();
  }

  private spawnEnemy(type: EnemyTypeId, atX?: number, atY?: number, atWp?: number, atDist?: number): void {
    const spec = ENEMY_SPECS[type];
    const scale = hpScale(this.wave) * (type === "boss" ? 1 + this.wave / 50 : 1);
    const start = PATH_POINTS[0] ?? [0, 0];
    this.enemies.push({
      id: this.nextEnemyId,
      type,
      spec,
      hp: Math.round(spec.hp * scale),
      maxHp: Math.round(spec.hp * scale),
      x: atX ?? start[0],
      y: atY ?? start[1],
      wp: atWp ?? 0,
      dist: atDist ?? 0,
      flashUntil: 0,
      slowed: false,
      dead: false,
    });
    this.nextEnemyId += 1;
  }

  private stepEnemies(dt: number): void {
    for (const enemy of this.enemies) {
      if (enemy.dead) continue;

      let speedFactor = 1;
      for (const tower of this.towers) {
        if (tower.type !== "checkpoint") continue;
        const tier = TOWER_SPECS.checkpoint.tiers[tower.tier];
        if (!tier) continue;
        if (Math.hypot(enemy.x - tower.x, enemy.y - tower.y) <= tier.range) {
          speedFactor = Math.min(speedFactor, tier.slowFactor);
        }
      }
      for (const cloud of this.gasClouds) {
        if (Math.hypot(enemy.x - cloud.x, enemy.y - cloud.y) <= cloud.radius) {
          speedFactor = Math.min(speedFactor, POWER_SPECS.teargas.slowFactor);
        }
      }
      enemy.slowed = speedFactor < 1;

      let remaining = enemy.spec.speed * speedScale(this.wave) * speedFactor * dt;
      while (remaining > 0) {
        const target = PATH_POINTS[enemy.wp + 1];
        if (!target) {
          this.enemyCrossed(enemy);
          break;
        }
        const dx = target[0] - enemy.x;
        const dy = target[1] - enemy.y;
        const distance = Math.hypot(dx, dy);
        if (distance <= remaining) {
          enemy.x = target[0];
          enemy.y = target[1];
          enemy.wp += 1;
          enemy.dist += distance;
          remaining -= distance;
        } else {
          enemy.x += (dx / distance) * remaining;
          enemy.y += (dy / distance) * remaining;
          enemy.dist += remaining;
          remaining = 0;
        }
      }
    }
  }

  private enemyCrossed(enemy: Enemy): void {
    enemy.dead = true;
    this.lives -= enemy.spec.lifeCost;
    this.streak = 0;
    this.shake = Math.max(this.shake, 10);
    this.audio.crossing();
    this.spawnFloat(enemy.spec.lifeCost > 1 ? `BREACH! -${enemy.spec.lifeCost}` : "BREACH! -1", WIDTH - 90, enemy.y, "#ff5f4a", 26);
    this.burst(WIDTH - 10, enemy.y, "#ff5f4a", 14, 160);
    this.hudDirty = true;
    if (this.lives <= 0) {
      this.lives = 0;
      this.gameOver();
    }
  }

  private stepTowers(dt: number): void {
    for (const tower of this.towers) {
      tower.cooldown -= dt;
      tower.recoil = Math.max(0, tower.recoil - dt * 6);

      const spec = TOWER_SPECS[tower.type];
      const tier = spec.tiers[tower.tier];
      if (!tier) continue;

      if (tower.type === "checkpoint") {
        if (tower.cooldown > 0) continue;
        let ticked = false;
        for (const enemy of this.enemies) {
          if (enemy.dead) continue;
          if (Math.hypot(enemy.x - tower.x, enemy.y - tower.y) <= tier.range) {
            this.damageEnemy(enemy, tier.damage, false);
            ticked = true;
          }
        }
        tower.cooldown = 1 / tier.fireRate;
        if (ticked) this.burst(tower.x, tower.y, "#c9a227", 3, 60);
        continue;
      }

      if (tower.type === "orbital") {
        if (tower.cooldown > 0) continue;
        this.fireOrbital(tower, tier);
        continue;
      }

      let target: Enemy | null = null;
      for (const enemy of this.enemies) {
        if (enemy.dead) continue;
        if (Math.hypot(enemy.x - tower.x, enemy.y - tower.y) > tier.range) continue;
        if (!target || enemy.dist > target.dist) target = enemy;
      }

      if (tower.type !== "tesla") {
        const desiredAim = target
          ? Math.atan2(target.y - (tower.y + GUN_PIVOT_Y[tower.type]), target.x - tower.x)
          : Math.PI;
        const turnRate = !target ? 1.4 : tower.type === "mortar" ? 3.2 : 7;
        tower.aim = turnToward(tower.aim, desiredAim, turnRate * dt);

        if (tower.cooldown > 0) continue;
        if (!target) {
          tower.cooldown = 0.05;
          continue;
        }
        if (angleDelta(tower.aim, desiredAim) > 0.1) {
          tower.cooldown = 0.02;
          continue;
        }
      } else {
        if (tower.cooldown > 0) continue;
        if (!target) {
          tower.cooldown = 0.05;
          continue;
        }
      }

      tower.cooldown = 1 / tier.fireRate;
      tower.recoil = 1;
      const muzzle = this.gunMuzzle(tower);

      if (tower.type === "mortar") {
        this.shells.push({
          x0: muzzle.x,
          y0: muzzle.y,
          x1: target.x,
          y1: target.y,
          age: 0,
          duration: 0.65,
          damage: tier.damage,
          splash: tier.splash,
          cluster: tier.cluster ?? 0,
        });
        this.audio.mortarFire();
      } else if (tower.type === "tesla") {
        this.fireTesla(tower, tier, target);
      } else if (tower.type === "sniper" && (tier.pierce ?? 0) > 1) {
        this.firePiercing(tower, tier, target, muzzle);
      } else {
        const maxTargets = Math.max(1, tier.targets ?? 1);
        const volley =
          maxTargets > 1
            ? this.enemies
                .filter((enemy) => !enemy.dead && Math.hypot(enemy.x - tower.x, enemy.y - tower.y) <= tier.range)
                .sort((a, b) => b.dist - a.dist)
                .slice(0, maxTargets)
            : [target];
        for (const victim of volley) {
          this.tracers.push({
            x0: muzzle.x,
            y0: muzzle.y,
            x1: victim.x,
            y1: victim.y,
            age: 0,
            ttl: 0.09,
            color: tower.type === "sniper" ? "#ffd27a" : "#fff3c9",
          });
          this.damageEnemy(victim, tier.damage, true);
        }
        if (tower.type === "sniper") this.audio.snipe();
        else this.audio.shoot();
      }
    }
  }

  private firePiercing(tower: Tower, tier: TowerTier, primary: Enemy, muzzle: { x: number; y: number }): void {
    const pivotY = tower.y + GUN_PIVOT_Y[tower.type];
    const dx = primary.x - tower.x;
    const dy = primary.y - pivotY;
    const length = Math.hypot(dx, dy) || 1;
    const dirX = dx / length;
    const dirY = dy / length;
    const hits = this.enemies
      .filter((enemy) => !enemy.dead && Math.hypot(enemy.x - tower.x, enemy.y - tower.y) <= tier.range)
      .map((enemy) => {
        const relX = enemy.x - tower.x;
        const relY = enemy.y - pivotY;
        return { enemy, along: relX * dirX + relY * dirY, perp: Math.abs(relX * dirY - relY * dirX) };
      })
      .filter((entry) => entry.along > 0 && entry.perp <= 26)
      .sort((a, b) => a.along - b.along)
      .slice(0, tier.pierce ?? 1);
    let endX = primary.x;
    let endY = primary.y;
    for (const hit of hits) {
      this.damageEnemy(hit.enemy, tier.damage, true);
      endX = hit.enemy.x;
      endY = hit.enemy.y;
    }
    this.tracers.push({ x0: muzzle.x, y0: muzzle.y, x1: endX, y1: endY, age: 0, ttl: 0.12, color: "#ffd27a" });
    this.audio.snipe();
  }

  private fireTesla(tower: Tower, tier: TowerTier, primary: Enemy): void {
    const maxJumps = Math.max(1, tier.chain ?? 1);
    const chain: Enemy[] = [primary];
    let current = primary;
    while (chain.length < maxJumps) {
      let next: Enemy | null = null;
      let best = 150;
      for (const enemy of this.enemies) {
        if (enemy.dead || chain.includes(enemy)) continue;
        const distance = Math.hypot(enemy.x - current.x, enemy.y - current.y);
        if (distance <= best) {
          best = distance;
          next = enemy;
        }
      }
      if (!next) break;
      chain.push(next);
      current = next;
    }
    let fromX = tower.x;
    let fromY = tower.y - 10;
    chain.forEach((victim, jump) => {
      this.tracers.push({ x0: fromX, y0: fromY, x1: victim.x, y1: victim.y, age: 0, ttl: 0.14, color: "#8ad9ff" });
      this.damageEnemy(victim, tier.damage * Math.pow(0.85, jump), true);
      this.burst(victim.x, victim.y, "#8ad9ff", 3, 80);
      fromX = victim.x;
      fromY = victim.y;
    });
    this.audio.zap();
  }

  private fireOrbital(tower: Tower, tier: TowerTier): void {
    let target: Enemy | null = null;
    for (const enemy of this.enemies) {
      if (enemy.dead) continue;
      if (!target || enemy.hp > target.hp) target = enemy;
    }
    if (!target) {
      tower.cooldown = 0.05;
      return;
    }
    const focus = target;
    tower.cooldown = 1 / tier.fireRate;
    tower.recoil = 1;
    this.tracers.push({ x0: focus.x, y0: -30, x1: focus.x, y1: focus.y, age: 0, ttl: 0.16, color: "#ffe08a" });
    this.blasts.push({ x: focus.x, y: focus.y, radius: tier.splash, age: 0, ttl: 0.4, color: "#ffd27a" });
    this.burst(focus.x, focus.y, "#ffd27a", 18, 220);
    this.shake = Math.max(this.shake, 8);
    this.audio.beam();
    for (const enemy of this.enemies) {
      if (enemy.dead) continue;
      if (Math.hypot(enemy.x - focus.x, enemy.y - focus.y) <= tier.splash + enemy.spec.radius) {
        this.damageEnemy(enemy, tier.damage, true);
      }
    }
  }

  private gunMuzzle(tower: Tower): { x: number; y: number } {
    const length =
      tower.type === "sniper"
        ? 18 + tower.tier * 5
        : tower.type === "mortar"
          ? 16 + tower.tier * 2
          : tower.type === "watchtower"
            ? 11 + tower.tier * 2
            : 13;
    return {
      x: tower.x + Math.cos(tower.aim) * length,
      y: tower.y + GUN_PIVOT_Y[tower.type] + Math.sin(tower.aim) * length,
    };
  }

  private stepShells(dt: number): void {
    const landed: Shell[] = [];
    for (const shell of this.shells) {
      shell.age += dt;
      if (shell.age >= shell.duration) landed.push(shell);
    }
    if (landed.length === 0) return;
    this.shells = this.shells.filter((shell) => shell.age < shell.duration);
    for (const shell of landed) {
      this.blasts.push({ x: shell.x1, y: shell.y1, radius: shell.splash, age: 0, ttl: 0.35, color: "#ff9a3d" });
      this.burst(shell.x1, shell.y1, "#ff9a3d", 14, 200);
      this.shake = Math.max(this.shake, 7);
      this.audio.explosion(0.6);
      for (const enemy of this.enemies) {
        if (enemy.dead) continue;
        if (Math.hypot(enemy.x - shell.x1, enemy.y - shell.y1) <= shell.splash + enemy.spec.radius) {
          this.damageEnemy(enemy, shell.damage, true);
        }
      }
      if (shell.cluster > 0) {
        for (let bomb = 0; bomb < shell.cluster; bomb += 1) {
          const angle = this.random.next() * Math.PI * 2;
          const spread = shell.splash * (0.35 + this.random.next() * 0.55);
          const bx = shell.x1 + Math.cos(angle) * spread;
          const by = shell.y1 + Math.sin(angle) * spread;
          this.blasts.push({ x: bx, y: by, radius: 62, age: 0, ttl: 0.3, color: "#ffca7a" });
          this.burst(bx, by, "#ffca7a", 8, 150);
          for (const enemy of this.enemies) {
            if (enemy.dead) continue;
            if (Math.hypot(enemy.x - bx, enemy.y - by) <= 62 + enemy.spec.radius) {
              this.damageEnemy(enemy, shell.damage * 0.45, true);
            }
          }
        }
        this.audio.explosion(0.4);
      }
    }
  }

  private damageEnemy(enemy: Enemy, amount: number, showNumber: boolean): void {
    if (enemy.dead || this.phase !== "playing") return;
    enemy.hp -= amount;
    enemy.flashUntil = this.time + 0.08;
    if (showNumber && amount >= 5) {
      this.spawnFloat(String(Math.round(amount)), enemy.x, enemy.y - enemy.spec.radius - 6, "#ffffff", 14);
    }
    if (enemy.hp <= 0) this.killEnemy(enemy);
  }

  private killEnemy(enemy: Enemy): void {
    if (enemy.dead) return;
    enemy.dead = true;
    const bounty = bountyFor(enemy.spec, this.streak, this.wave);
    this.cash += bounty;
    this.score += enemy.spec.bounty * 10 * comboMultiplier(this.streak);
    this.streak += 1;
    this.audio.kill(this.streak);

    if (enemy.type === "boss") {
      this.shake = Math.max(this.shake, 22);
      this.spawnFloat("SMUGGLER BUSTED!", enemy.x, enemy.y - 40, "#ffd27a", 34);
      this.blasts.push({ x: enemy.x, y: enemy.y, radius: 120, age: 0, ttl: 0.6, color: "#ffd27a" });
      this.burst(enemy.x, enemy.y, "#ffd27a", 34, 300);
      this.audio.explosion(1);
    } else {
      this.spawnFloat(pickQuip(this.random), enemy.x, enemy.y - 26, "#9be27a", 20);
      this.burst(enemy.x, enemy.y, ENEMY_COLORS[enemy.type].body, 10, 140);
    }
    this.spawnFloat(`+$${bounty}`, enemy.x, enemy.y - 4, "#ffe08a", 15);

    if (enemy.type === "splitter") {
      for (let offset = 0; offset < 2; offset += 1) {
        this.spawnEnemy("kid", enemy.x + (offset === 0 ? -8 : 8), enemy.y + (offset === 0 ? 4 : -4), enemy.wp, enemy.dist);
      }
    }
    this.hudDirty = true;
  }

  private sweepDead(): void {
    if (this.enemies.some((enemy) => enemy.dead)) {
      this.enemies = this.enemies.filter((enemy) => !enemy.dead);
    }
  }

  private spawnFloat(text: string, x: number, y: number, color: string, size: number): void {
    this.floats.push({ x, y, text, color, size, age: 0, ttl: 0.9 });
  }

  private burst(x: number, y: number, color: string, count: number, speed: number): void {
    for (let index = 0; index < count; index += 1) {
      const angle = this.random.next() * Math.PI * 2;
      const velocity = speed * (0.4 + this.random.next() * 0.6);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size: 2 + this.random.next() * 3,
        color,
        age: 0,
        ttl: 0.35 + this.random.next() * 0.35,
      });
    }
  }

  private syncBuildBar(): void {
    for (const [type, button] of this.towerButtons) {
      const cost = TOWER_SPECS[type].tiers[0]?.cost ?? 0;
      button.classList.toggle("active", this.placing === type);
      button.classList.toggle("broke", this.cash < cost);
    }
    for (const [power, button] of this.powerButtons) {
      button.classList.toggle("active", this.armedPower === power);
    }
  }

  private syncPowerButtons(): void {
    for (const [power, button] of this.powerButtons) {
      const spec = POWER_SPECS[power];
      const remaining = this.powerReadyAt[power] - this.time;
      const label = button.querySelector(".power-cooldown");
      if (remaining <= 0) {
        button.classList.remove("cooling");
        if (label && label.textContent !== "READY") label.textContent = "READY";
      } else {
        button.classList.add("cooling");
        const text = `${Math.ceil(remaining)}s`;
        if (label && label.textContent !== text) label.textContent = text;
        const fraction = Math.min(1, remaining / spec.cooldown);
        button.style.setProperty("--cooldown", `${fraction * 100}%`);
      }
    }
  }

  private syncNextWaveButton(): void {
    const button = this.refs.nextWaveButton;
    if (this.waveActive) {
      button.disabled = true;
      button.textContent = `WAVE ${this.wave} IN PROGRESS`;
    } else {
      button.disabled = false;
      button.textContent = `NEXT WAVE (${Math.max(0, Math.ceil(this.idleTimer))}s)`;
    }
  }

  private syncHud(): void {
    this.hudDirty = false;
    this.refs.cash.textContent = `$${this.cash.toLocaleString()}`;
    this.refs.wave.textContent = this.wave === 0 ? "—" : String(this.wave);
    this.refs.lives.textContent = "▮".repeat(Math.max(0, Math.min(this.lives, 20))) || "✕";
    this.refs.score.textContent = this.score.toLocaleString();
    this.refs.best.textContent = this.bestScore.toLocaleString();
    const multiplier = comboMultiplier(this.streak);
    this.refs.streak.textContent = this.streak > 0 ? `${this.streak} ×${multiplier}` : "—";
    this.syncNextWaveButton();
    this.syncPanelAffordability();
  }

  private resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.rotated = window.innerWidth < window.innerHeight;
    this.refs.shell.classList.toggle("rotated", this.rotated);
    this.refs.canvas.width = (this.rotated ? HEIGHT : WIDTH) * dpr;
    this.refs.canvas.height = (this.rotated ? WIDTH : HEIGHT) * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.fitStageToViewport();
  }

  private fitStageToViewport(): void {
    const stage = this.refs.shell.querySelector<HTMLElement>(".game-stage");
    if (!stage) return;
    if (!this.rotated) {
      stage.style.width = "";
      return;
    }
    const shellStyle = getComputedStyle(this.refs.shell);
    const gap = parseFloat(shellStyle.rowGap) || 0;
    const chrome = [".game-header", ".command-bar", ".game-footer"].reduce(
      (total, selector) => total + (this.refs.shell.querySelector<HTMLElement>(selector)?.offsetHeight ?? 0),
      parseFloat(shellStyle.paddingTop) + parseFloat(shellStyle.paddingBottom) + gap * 2 + 6,
    );
    const availableHeight = window.innerHeight - chrome;
    const maxWidth = this.refs.shell.clientWidth - parseFloat(shellStyle.paddingLeft) - parseFloat(shellStyle.paddingRight);
    const width = Math.max(200, Math.min(maxWidth, availableHeight * (HEIGHT / WIDTH)));
    stage.style.width = `${Math.floor(width)}px`;
  }

  private render(): void {
    const ctx = this.ctx;
    ctx.save();
    if (this.shake > 0.2) {
      ctx.translate((this.random.next() - 0.5) * this.shake, (this.random.next() - 0.5) * this.shake);
    }
    if (this.rotated) {
      ctx.translate(HEIGHT, 0);
      ctx.rotate(Math.PI / 2);
    }

    this.drawTerrain(ctx);
    this.drawPath(ctx);
    this.drawWall(ctx);
    this.drawGas(ctx);
    this.drawPlacementHints(ctx);
    this.drawTowers(ctx);
    this.drawEnemies(ctx);
    this.drawShells(ctx);
    this.drawEffects(ctx);

    ctx.restore();
  }

  private drawTerrain(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#b99a63";
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    const decor = new SeededRandom(1337);
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (!isBuildable(col, row)) continue;
        const roll = decor.next();
        const x = (col + 0.5) * TILE;
        const y = (row + 0.5) * TILE;
        if (roll < 0.16) {
          ctx.fillStyle = "rgba(90, 70, 40, 0.35)";
          ctx.beginPath();
          ctx.ellipse(x + (decor.next() - 0.5) * 30, y + (decor.next() - 0.5) * 30, 5 + decor.next() * 6, 3 + decor.next() * 4, decor.next() * 3, 0, Math.PI * 2);
          ctx.fill();
        } else if (roll < 0.24) {
          ctx.strokeStyle = "rgba(60, 90, 40, 0.5)";
          ctx.lineWidth = 3;
          const cx = x + (decor.next() - 0.5) * 26;
          const cy = y + (decor.next() - 0.5) * 26;
          ctx.beginPath();
          ctx.moveTo(cx, cy + 8);
          ctx.lineTo(cx, cy - 8);
          ctx.moveTo(cx, cy - 2);
          ctx.lineTo(cx - 5, cy - 6);
          ctx.moveTo(cx, cy + 2);
          ctx.lineTo(cx + 5, cy - 2);
          ctx.stroke();
        }
      }
    }
  }

  private drawPath(ctx: CanvasRenderingContext2D): void {
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#8a6f45";
    ctx.lineWidth = TILE * 0.72;
    ctx.beginPath();
    PATH_POINTS.forEach(([x, y], index) => {
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    ctx.strokeStyle = "#a8895a";
    ctx.lineWidth = TILE * 0.58;
    ctx.beginPath();
    PATH_POINTS.forEach(([x, y], index) => {
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    ctx.strokeStyle = "rgba(70, 50, 25, 0.5)";
    ctx.lineWidth = 3;
    ctx.setLineDash([14, 18]);
    ctx.beginPath();
    PATH_POINTS.forEach(([x, y], index) => {
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);
  }

  private drawWall(ctx: CanvasRenderingContext2D): void {
    const gapTop = 11 * TILE - 8;
    const gapBottom = 12 * TILE + 8;
    ctx.fillStyle = "#6d6f75";
    ctx.fillRect(WIDTH - 26, 0, 26, gapTop);
    ctx.fillRect(WIDTH - 26, gapBottom, 26, HEIGHT - gapBottom);
    ctx.fillStyle = "#585a60";
    for (let y = 0; y < HEIGHT; y += 32) {
      if (y > gapTop - 16 && y < gapBottom) continue;
      ctx.fillRect(WIDTH - 26, y, 26, 4);
    }
    ctx.strokeStyle = "#3c3e44";
    ctx.lineWidth = 2;
    for (let y = 10; y < HEIGHT; y += 24) {
      if (y > gapTop - 16 && y < gapBottom) continue;
      ctx.beginPath();
      ctx.moveTo(WIDTH - 30, y);
      ctx.lineTo(WIDTH - 34, y - 8);
      ctx.stroke();
    }

    ctx.fillStyle = "#e8e3d3";
    ctx.font = "bold 15px system-ui, sans-serif";
    ctx.textAlign = "right";
    ctx.save();
    ctx.translate(WIDTH - 34, 40);
    ctx.fillText("THE WALL", 0, 0);
    ctx.restore();

    ctx.fillStyle = "rgba(61, 47, 26, 0.85)";
    for (let chevron = 0; chevron < 2; chevron += 1) {
      const cx = 8 + chevron * 14;
      ctx.beginPath();
      ctx.moveTo(cx, TILE * 1.5 - 12);
      ctx.lineTo(cx + 10, TILE * 1.5);
      ctx.lineTo(cx, TILE * 1.5 + 12);
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = "#e8e3d3";
    ctx.font = "bold 12px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.save();
    ctx.translate(40, TILE * 0.7);
    ctx.rotate(-0.06);
    ctx.fillText("EL OTRO LADO →", 0, 0);
    ctx.restore();
  }

  private drawGas(ctx: CanvasRenderingContext2D): void {
    for (const cloud of this.gasClouds) {
      const remaining = Math.max(0, cloud.until - this.time);
      const alpha = Math.min(0.35, remaining * 0.3);
      ctx.fillStyle = `rgba(168, 217, 122, ${alpha})`;
      ctx.beginPath();
      ctx.arc(cloud.x, cloud.y, cloud.radius * (1 + Math.sin(this.time * 6) * 0.04), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(120, 170, 80, 0.5)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  private drawPlacementHints(ctx: CanvasRenderingContext2D): void {
    if (this.phase !== "playing") return;

    if (this.placing && this.hover) {
      const { col, row } = this.hover;
      const occupied = this.towers.some((tower) => tower.col === col && tower.row === row);
      const ok = isBuildable(col, row) && !occupied && this.cash >= (TOWER_SPECS[this.placing].tiers[0]?.cost ?? 0);
      ctx.fillStyle = ok ? "rgba(120, 226, 122, 0.35)" : "rgba(255, 95, 74, 0.35)";
      ctx.fillRect(col * TILE + 2, row * TILE + 2, TILE - 4, TILE - 4);
      if (ok) {
        const tierSpec = TOWER_SPECS[this.placing].tiers[0];
        const range = tierSpec?.range ?? 0;
        if (tierSpec && !tierSpec.global) {
          ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc((col + 0.5) * TILE, (row + 0.5) * TILE, range, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }

    if (this.armedPower) {
      const spec = POWER_SPECS[this.armedPower];
      ctx.fillStyle = "rgba(255, 255, 255, 0.06)";
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      if (this.hover) {
        const x = (this.hover.col + 0.5) * TILE;
        const y = (this.hover.row + 0.5) * TILE;
        ctx.strokeStyle = this.armedPower === "drone" ? "rgba(255, 179, 71, 0.9)" : "rgba(168, 217, 122, 0.9)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, spec.radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - 12, y);
        ctx.lineTo(x + 12, y);
        ctx.moveTo(x, y - 12);
        ctx.lineTo(x, y + 12);
        ctx.stroke();
      }
    }

    if (this.selected) {
      const spec = TOWER_SPECS[this.selected.type];
      const tier = spec.tiers[this.selected.tier];
      if (tier && !tier.global) {
        ctx.strokeStyle = "rgba(255, 224, 138, 0.8)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(this.selected.x, this.selected.y, tier.range, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.strokeStyle = "#ffe08a";
      ctx.lineWidth = 3;
      ctx.strokeRect(this.selected.col * TILE + 2, this.selected.row * TILE + 2, TILE - 4, TILE - 4);
    }
  }

  private drawTowers(ctx: CanvasRenderingContext2D): void {
    for (const tower of this.towers) {
      const { x, y } = tower;
      const tier = tower.tier;
      ctx.save();
      ctx.translate(x, y);

      ctx.fillStyle = "rgba(0, 0, 0, 0.22)";
      ctx.beginPath();
      ctx.ellipse(0, 16, 18, 6, 0, 0, Math.PI * 2);
      ctx.fill();

      const color = TOWER_COLORS[tower.type];
      if (tower.type === "watchtower") {
        ctx.strokeStyle = "#4e3a20";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(-10, 16);
        ctx.lineTo(-5, -6);
        ctx.moveTo(10, 16);
        ctx.lineTo(5, -6);
        ctx.stroke();
        if (tier >= 3) {
          ctx.fillStyle = "#55503f";
          ctx.beginPath();
          ctx.arc(0, 6, 20, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = color;
        const cabin = 16 + tier * 2;
        ctx.fillRect(-cabin / 2, -18 - tier * 2, cabin, cabin * 0.8);
        ctx.fillStyle = "#33241a";
        ctx.fillRect(-cabin / 2 - 2, -21 - tier * 2, cabin + 4, 4);
        ctx.save();
        ctx.translate(0, GUN_PIVOT_Y.watchtower);
        ctx.rotate(tower.aim);
        const barrels = 1 + (tier >= 2 ? 1 : 0) + (tier >= 3 ? 1 : 0);
        for (let barrel = 0; barrel < barrels; barrel += 1) {
          const angleOffset = (barrel - (barrels - 1) / 2) * 0.14;
          const reach = 11 + tier * 2 - tower.recoil * 2.5;
          ctx.strokeStyle = "#2c241a";
          ctx.lineWidth = 3.5;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(Math.cos(angleOffset) * reach, Math.sin(angleOffset) * reach);
          ctx.stroke();
        }
        ctx.restore();
      } else if (tower.type === "sniper") {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(0, 0, 14 + tier, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.rotate(tower.aim);
        const reach = 18 + tier * 5 - tower.recoil * 3;
        ctx.strokeStyle = "#2c343c";
        ctx.lineWidth = 4 + tier * 0.6;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(reach, 0);
        ctx.stroke();
        if (tier >= 2) {
          ctx.lineWidth = 6.5;
          ctx.beginPath();
          ctx.moveTo(reach - 4, 0);
          ctx.lineTo(reach, 0);
          ctx.stroke();
        }
        if (tier >= 3) {
          ctx.strokeStyle = "#9fd8ff";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(4, -3);
          ctx.lineTo(22, -3);
          ctx.moveTo(4, 3);
          ctx.lineTo(22, 3);
          ctx.stroke();
        }
        ctx.restore();
        ctx.fillStyle = "#9fd8ff";
        ctx.beginPath();
        ctx.arc(0, 0, 4, 0, Math.PI * 2);
        ctx.fill();
      } else if (tower.type === "checkpoint") {
        const half = 12 + tier * 2;
        ctx.fillStyle = "#d8d3c3";
        ctx.fillRect(-half, -10 - tier * 2, half * 2, 22 + tier * 2);
        ctx.fillStyle = "#b33a2e";
        ctx.fillRect(-half, -10 - tier * 2, half * 2, 5);
        ctx.strokeStyle = color;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(-22, 12);
        ctx.lineTo(22, 12);
        ctx.stroke();
        if (tier >= 3) {
          ctx.beginPath();
          ctx.moveTo(-22, -2);
          ctx.lineTo(22, -2);
          ctx.stroke();
        }
        ctx.strokeStyle = "#d8d3c3";
        ctx.lineWidth = 2;
        for (let stripe = -18; stripe <= 18; stripe += 9) {
          ctx.beginPath();
          ctx.moveTo(stripe, 9);
          ctx.lineTo(stripe + 4, 15);
          ctx.stroke();
        }
        if (tier >= 2) {
          ctx.strokeStyle = "#8f8a7a";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(half - 4, -10 - tier * 2);
          ctx.lineTo(half - 4, -24 - tier * 2);
          ctx.stroke();
          ctx.fillStyle = "#ff5f4a";
          ctx.beginPath();
          ctx.arc(half - 4, -25 - tier * 2, 2.4, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (tower.type === "tesla") {
        ctx.fillStyle = "#26333d";
        ctx.beginPath();
        ctx.arc(0, 8, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(0, 10);
        ctx.lineTo(0, -10);
        ctx.stroke();
        const coils = 1 + tier;
        ctx.lineWidth = 2;
        for (let coil = 0; coil < coils; coil += 1) {
          ctx.strokeStyle = coil % 2 === 0 ? "#8ad9ff" : color;
          ctx.beginPath();
          ctx.ellipse(0, -6 - coil * 6, 6 + tier, 2.5, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.fillStyle = "#cfeaff";
        ctx.beginPath();
        ctx.arc(0, -10 - coils * 6, 4 + tier, 0, Math.PI * 2);
        ctx.fill();
      } else if (tower.type === "orbital") {
        ctx.fillStyle = "#3a3128";
        ctx.beginPath();
        ctx.arc(0, 8, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#8a7440";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(0, 10);
        ctx.lineTo(0, -4);
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(0, -10 - tier * 2, 12 + tier * 3, 5 + tier, -0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#6a5426";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(0, -10 - tier * 2, 12 + tier * 3, 5 + tier, -0.5, 0, Math.PI * 2);
        ctx.stroke();
        if (tier >= 2) {
          ctx.strokeStyle = "#c9a227";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(10, -6);
          ctx.lineTo(16, -16 - tier * 2);
          ctx.stroke();
        }
        ctx.fillStyle = "#ffe08a";
        ctx.beginPath();
        ctx.arc(0, -10 - tier * 2, 3 + tier, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = "#33382e";
        ctx.beginPath();
        ctx.arc(0, 0, 16 + tier, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(0, 0, 11 + tier * 0.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.rotate(tower.aim);
        const tubes = 1 + (tier >= 2 ? 1 : 0) + (tier >= 3 ? 1 : 0);
        for (let tube = 0; tube < tubes; tube += 1) {
          const angleOffset = (tube - (tubes - 1) / 2) * 0.16;
          const reach = 16 + tier * 2 - tower.recoil * 4;
          ctx.strokeStyle = "#20241d";
          ctx.lineWidth = 6 + tier;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(Math.cos(angleOffset) * reach, Math.sin(angleOffset) * reach);
          ctx.stroke();
        }
        ctx.restore();
        if (tier >= 3) {
          ctx.fillStyle = "#6a705f";
          for (let rack = 0; rack < 4; rack += 1) {
            ctx.beginPath();
            ctx.arc(-10 + rack * 6, 14, 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      for (let pip = 0; pip <= tier; pip += 1) {
        ctx.fillStyle = "#ffe08a";
        ctx.beginPath();
        ctx.arc((pip - tier / 2) * 9, 22, 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  private drawEnemies(ctx: CanvasRenderingContext2D): void {
    for (const enemy of this.enemies) {
      const colors = ENEMY_COLORS[enemy.type];
      const flash = this.time < enemy.flashUntil;
      ctx.save();
      ctx.translate(enemy.x, enemy.y);

      ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
      ctx.beginPath();
      ctx.ellipse(0, enemy.spec.radius * 0.8, enemy.spec.radius * 0.9, enemy.spec.radius * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();

      if (enemy.type === "boss") {
        ctx.fillStyle = flash ? "#ffffff" : colors.body;
        ctx.fillRect(-34, -18, 68, 34);
        ctx.fillStyle = flash ? "#ffffff" : colors.trim;
        ctx.fillRect(-34, -18, 68, 9);
        ctx.fillStyle = "#1a1a1a";
        ctx.beginPath();
        ctx.arc(-20, 17, 7, 0, Math.PI * 2);
        ctx.arc(20, 17, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffe08a";
        for (let win = -26; win <= 22; win += 12) {
          ctx.fillRect(win, -14, 8, 6);
        }
      } else {
        ctx.fillStyle = flash ? "#ffffff" : colors.body;
        ctx.beginPath();
        ctx.arc(0, 0, enemy.spec.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = flash ? "#ffffff" : colors.trim;
        ctx.beginPath();
        ctx.moveTo(-enemy.spec.radius, -enemy.spec.radius * 0.35);
        ctx.lineTo(enemy.spec.radius, -enemy.spec.radius * 0.35);
        ctx.lineTo(0, -enemy.spec.radius * 1.5);
        ctx.closePath();
        ctx.fill();
        if (enemy.type === "splitter") {
          ctx.fillStyle = "#d8a7c4";
          ctx.beginPath();
          ctx.arc(-4, 4, 3, 0, Math.PI * 2);
          ctx.arc(4, 4, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (enemy.slowed) {
        ctx.strokeStyle = "rgba(140, 200, 255, 0.8)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, enemy.spec.radius + 3, 0, Math.PI * 2);
        ctx.stroke();
      }

      if (enemy.hp < enemy.maxHp) {
        const width = enemy.type === "boss" ? 70 : 30;
        const ratio = Math.max(0, enemy.hp / enemy.maxHp);
        ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
        ctx.fillRect(-width / 2, -enemy.spec.radius - 12, width, 5);
        ctx.fillStyle = ratio > 0.5 ? "#7ae282" : ratio > 0.25 ? "#ffcf5a" : "#ff6b5a";
        ctx.fillRect(-width / 2, -enemy.spec.radius - 12, width * ratio, 5);
      }
      ctx.restore();
    }
  }

  private drawShells(ctx: CanvasRenderingContext2D): void {
    for (const shell of this.shells) {
      const progress = Math.min(1, shell.age / shell.duration);
      const x = shell.x0 + (shell.x1 - shell.x0) * progress;
      const y = shell.y0 + (shell.y1 - shell.y0) * progress - Math.sin(progress * Math.PI) * 60;
      ctx.fillStyle = "#2a2a2a";
      ctx.beginPath();
      ctx.arc(x, y, 4.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(255, 180, 90, 0.8)";
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawEffects(ctx: CanvasRenderingContext2D): void {
    for (const tracer of this.tracers) {
      const alpha = 1 - tracer.age / tracer.ttl;
      ctx.strokeStyle = tracer.color;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(tracer.x0, tracer.y0);
      ctx.lineTo(tracer.x1, tracer.y1);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    for (const blast of this.blasts) {
      const progress = blast.age / blast.ttl;
      ctx.globalAlpha = Math.max(0, 0.7 - progress * 0.7);
      ctx.fillStyle = blast.color;
      ctx.beginPath();
      ctx.arc(blast.x, blast.y, blast.radius * (0.3 + progress * 0.9), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    for (const particle of this.particles) {
      ctx.globalAlpha = Math.max(0, 1 - particle.age / particle.ttl);
      ctx.fillStyle = particle.color;
      ctx.fillRect(particle.x - particle.size / 2, particle.y - particle.size / 2, particle.size, particle.size);
      ctx.globalAlpha = 1;
    }

    ctx.textAlign = "center";
    for (const float of this.floats) {
      const progress = float.age / float.ttl;
      ctx.globalAlpha = Math.max(0, 1 - progress);
      ctx.font = `800 ${float.size}px system-ui, sans-serif`;
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.7)";
      const y = float.y - progress * 34;
      ctx.strokeText(float.text, float.x, y);
      ctx.fillStyle = float.color;
      ctx.fillText(float.text, float.x, y);
      ctx.globalAlpha = 1;
    }
  }
}
